import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowDown, Plus, Trash2, Ban, ShieldCheck, KeyRound, Copy, Check, Download, ChevronDown, CheckCircle2,
} from 'lucide-react';
import { useClient } from '../hooks/useClients.js';
import { useAuthStore } from '../store/auth.store.js';
import {
  useCertificates, useRegisterCertificate, useRevokeCertificate,
  useGenerateCsr, useCompleteCertificate,
} from '../hooks/useCertificates.js';
import { usePointsOfSale, useCreatePos, useSyncPos } from '../hooks/usePointsOfSale.js';
import { useApiKeys, useGenerateApiKey, useRevokeApiKey } from '../hooks/useApiKeys.js';
import PageHeader from '../components/PageHeader.jsx';
import Table from '../components/Table.jsx';
import Badge from '../components/Badge.jsx';
import Button from '../components/Button.jsx';
import Modal from '../components/Modal.jsx';
import Input from '../components/Input.jsx';
import FileDropInput from '../components/FileDropInput.jsx';

const CERT_MARKERS = ['-----BEGIN CERTIFICATE-----'];
const KEY_MARKERS = ['-----BEGIN PRIVATE KEY-----', '-----BEGIN RSA PRIVATE KEY-----', '-----BEGIN EC PRIVATE KEY-----'];

const ENV_VARIANT = { testing: 'warning', production: 'success' };

// El flujo real de ARCA para subir un CSR es distinto por entorno: testing
// (WSASS) pide pegar el CSR como texto; producción (Administrador de
// Certificados Digitales por alias) pide subir el .csr como archivo y
// devuelve el .crt para descargar, no para copiar.
function productionCsrSteps(onDownload) {
  return [
    <>Entrá a ARCA con la clave fiscal del CUIT de este cliente y buscá <strong>"Administrador de Certificados Digitales"</strong>.</>,
    <>Elegí (o creá) el alias correspondiente y entrá a su detalle — ahí vas a ver los certificados anteriores de ese alias, con su estado si ya vencieron.</>,
    <>Hacé click en <strong>"Agregar certificado"</strong>.</>,
    <>Subí el archivo <code>.csr</code> que acabás de descargar (si no lo ves en los botones de arriba, descargalo <button type="button" onClick={onDownload} className="text-accent underline hover:no-underline">acá</button>).</>,
    <>Confirmá con <strong>"Agregar certificado"</strong> de nuevo.</>,
    <>Te va a aparecer para descargar el <code>.crt</code> generado — descargalo.</>,
    <>Subí ese mismo <code>.crt</code> en el paso siguiente de este wizard.</>,
  ];
}

function testingCsrSteps(alias) {
  return [
    <>Entrá con la clave fiscal del CUIT de este cliente.</>,
    <>Buscá el servicio <strong>"WSASS - Autogestión Certificados Homologación"</strong>.</>,
    <>Hacé click en <strong>"Nuevo Certificado"</strong>.</>,
    <>En el campo <strong>"Nombre simbólico"</strong> usá el mismo alias que pusiste acá{alias ? <> (<code>{alias}</code>)</> : ''}.</>,
    <>Pegá el CSR de arriba donde te pida el certificate request.</>,
    <>Apretá <strong>"Crear DN..."</strong>.</>,
    <>Copiá el código que te devuelve y pegalo en el paso siguiente (ya arranca en modo "pegar texto").</>,
  ];
}

function StepDots({ step, total }) {
  return (
    <div className="flex items-center gap-1.5 mb-1">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-accent' : i < step ? 'w-1.5 bg-accent/50' : 'w-1.5 bg-border-strong'}`} />
      ))}
    </div>
  );
}

function GenerateCsrWizard({ clientId, open, onClose }) {
  const generateCsr = useGenerateCsr(clientId);
  const complete = useCompleteCertificate(clientId);
  const revoke = useRevokeCertificate(clientId);
  const [step, setStep] = useState(0); // 0 = datos, 1 = CSR, 2 = subir certificado
  const [form, setForm] = useState({ environment: 'testing', alias: '' });
  const [cert, setCert] = useState(null); // respuesta de generate-csr: { id, csr_pem, ... }
  const [copied, setCopied] = useState(false);
  const [certPem, setCertPem] = useState('');
  const [error, setError] = useState(null);

  const reset = () => {
    setStep(0);
    setForm({ environment: 'testing', alias: '' });
    setCert(null);
    setCopied(false);
    setCertPem('');
    setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await generateCsr.mutateAsync(form);
      setCert(result);
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al generar el CSR');
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(cert.csr_pem);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCsr = () => {
    const blob = new Blob([cert.csr_pem], { type: 'application/pkcs10' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.alias || 'certificado'}.csr`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await complete.mutateAsync({ certId: cert.id, certPem });
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al activar el certificado');
    }
  };

  const handleCancelPending = async () => {
    if (!confirm(`¿Cancelar y eliminar el certificado "${form.alias}"? Todavía no fue sincronizado con ARCA — se puede borrar sin problema.`)) return;
    try {
      await revoke.mutateAsync(cert.id);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar el certificado');
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Generar certificado nuevo" maxWidth="max-w-2xl">
      <StepDots step={step} total={3} />

      {step === 0 && (
        <form onSubmit={handleGenerate} className="space-y-3">
          <p className="text-xs text-text-muted">
            Generamos la clave privada y el CSR acá mismo — la clave se cifra y se guarda de
            una, nunca la vas a ver en texto plano ni vos ni nosotros de nuevo.
          </p>
          <Input as="select" label="Entorno" value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })}>
            <option value="testing">Testing (homologación)</option>
            <option value="production">Producción</option>
          </Input>
          <Input label="Alias" value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} placeholder="produccion-2026" required />
          {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={handleClose}>Cancelar</Button>
            <Button type="submit" loading={generateCsr.isPending}>Generar CSR <ArrowRight size={14} /></Button>
          </div>
        </form>
      )}

      {step === 1 && (
        <div className="space-y-3">
          <div className="relative">
            <textarea readOnly rows={4} value={cert.csr_pem} className="input-base font-mono text-xs whitespace-nowrap overflow-x-auto" />
            <div className="absolute top-2 right-4 flex gap-1">
              <button
                onClick={handleDownloadCsr}
                className="p-1.5 rounded-md bg-bg-elevated border border-border hover:border-accent text-text-muted hover:text-accent"
                title="Descargar .csr"
              >
                <Download size={14} />
              </button>
              <button
                onClick={handleCopy}
                className="p-1.5 rounded-md bg-bg-elevated border border-border hover:border-accent text-text-muted hover:text-accent"
                title="Copiar"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          <div className="p-3 bg-bg-elevated rounded-lg border border-border space-y-2">
            <p className="text-xs font-medium text-text-secondary">
              Ahora en {form.environment === 'production' ? 'el portal de ARCA (producción)' : 'el portal de ARCA — entorno de testing/homologación'}:
            </p>
            <ol className="text-xs text-text-muted space-y-1 list-decimal list-inside">
              {(form.environment === 'production' ? productionCsrSteps(handleDownloadCsr) : testingCsrSteps(form.alias)).map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
          </div>
          <p className="text-xs text-text-muted">
            Si cerrás esta ventana antes de terminar, no se pierde nada — el certificado queda
            como <Badge variant="warning">pending</Badge> y podés retomarlo con el botón
            "Completar" en la tabla.
          </p>
          <div className="flex justify-between gap-2 pt-2">
            <Button type="button" variant="danger" loading={revoke.isPending} onClick={handleCancelPending}>
              <Trash2 size={14} /> Cancelar
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={handleClose}>Terminar después <ArrowDown size={14} /></Button>
              <Button onClick={() => setStep(2)}>Ya tengo el certificado <ArrowRight size={14} /></Button>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <form onSubmit={handleComplete} className="space-y-3">
          <FileDropInput
            label="Certificado firmado por ARCA (.crt)"
            expectedMarkers={CERT_MARKERS}
            value={certPem}
            onChange={setCertPem}
            defaultPaste={form.environment !== 'production'}
            hint={form.environment === 'production'
              ? 'Es el .crt que acabás de descargar de ARCA — lo validamos contra la clave que ya generamos.'
              : 'Es el que acabás de copiar de ARCA — lo validamos contra la clave que ya generamos.'}
          />
          {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setStep(1)}><ArrowLeft size={14} /> Atrás</Button>
            <Button type="submit" loading={complete.isPending} disabled={!certPem}><CheckCircle2 size={14} /> Activar certificado</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function CompleteCertificateModal({ clientId, certificate, onClose }) {
  const complete = useCompleteCertificate(clientId);
  const [certPem, setCertPem] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleClose = () => {
    setCertPem('');
    setError(null);
    setCopied(false);
    setShowHelp(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await complete.mutateAsync({ certId: certificate.id, certPem });
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al completar el certificado');
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(certificate.csr_pem);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCsr = () => {
    const blob = new Blob([certificate.csr_pem], { type: 'application/pkcs10' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${certificate.alias || 'certificado'}.csr`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isProduction = certificate?.environment === 'production';

  return (
    <Modal open={!!certificate} onClose={handleClose} title={`Completar ${certificate?.alias || ''}`} maxWidth="max-w-2xl">
      <form onSubmit={handleSubmit} className="space-y-3">
        {certificate?.csr_pem && (
          <>
            <div className="relative">
              <textarea readOnly rows={4} value={certificate.csr_pem} className="input-base font-mono text-xs whitespace-nowrap overflow-x-auto" />
              <div className="absolute top-2 right-4 flex gap-1">
                <button type="button" onClick={handleDownloadCsr}
                  className="p-1.5 rounded-md bg-bg-elevated border border-border hover:border-accent text-text-muted hover:text-accent"
                  title="Descargar .csr">
                  <Download size={14} />
                </button>
                <button type="button" onClick={handleCopy}
                  className="p-1.5 rounded-md bg-bg-elevated border border-border hover:border-accent text-text-muted hover:text-accent"
                  title="Copiar">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowHelp((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 bg-bg-elevated text-xs font-medium text-text-secondary hover:text-text-primary"
              >
                Ayuda: qué hacer ahora en ARCA
                <ChevronDown size={14} className={`transition-transform ${showHelp ? 'rotate-180' : ''}`} />
              </button>
              {showHelp && (
                <div className="p-3 space-y-2 border-t border-border">
                  <p className="text-xs font-medium text-text-secondary">
                    Ahora en {isProduction ? 'el portal de ARCA (producción)' : 'el portal de ARCA — entorno de testing/homologación'}:
                  </p>
                  <ol className="text-xs text-text-muted space-y-1 list-decimal list-inside">
                    {(isProduction ? productionCsrSteps(handleDownloadCsr) : testingCsrSteps(certificate?.alias)).map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </>
        )}
        <FileDropInput
          label="Certificado firmado por ARCA (.crt)"
          expectedMarkers={CERT_MARKERS}
          value={certPem}
          onChange={setCertPem}
          defaultPaste={!isProduction}
          hint="El sistema valida que corresponda a la clave que ya generamos para este certificado."
        />
        {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button type="submit" loading={complete.isPending} disabled={!certPem}><CheckCircle2 size={14} /> Activar</Button>
        </div>
      </form>
    </Modal>
  );
}

function RegisterCertificateModal({ clientId, open, onClose }) {
  const register = useRegisterCertificate(clientId);
  const [form, setForm] = useState({ environment: 'testing', alias: '', certPem: '', keyPem: '' });
  const [error, setError] = useState(null);

  const handleClose = () => {
    setForm({ environment: 'testing', alias: '', certPem: '', keyPem: '' });
    setError(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await register.mutateAsync(form);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrar el certificado');
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Cargar certificado + clave existentes">
      <form onSubmit={handleSubmit} className="space-y-3">
        <p className="text-xs text-text-muted">
          Para cuando ya generaste la clave y el CSR por tu cuenta (openssl u otra herramienta).
          Si no tenés nada todavía, usá "Generar certificado nuevo" en su lugar.
        </p>
        <Input as="select" label="Entorno" value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })}>
          <option value="testing">Testing (homologación)</option>
          <option value="production">Producción</option>
        </Input>
        <Input label="Alias" value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} required />
        <FileDropInput
          label="Certificado (.crt/.pem)"
          expectedMarkers={CERT_MARKERS}
          value={form.certPem}
          onChange={(certPem) => setForm({ ...form, certPem })}
          hint="El certificado emitido por ARCA (WSASS), público — no contiene la clave privada."
        />
        <FileDropInput
          label="Clave privada (.key/.pem)"
          expectedMarkers={KEY_MARKERS}
          value={form.keyPem}
          onChange={(keyPem) => setForm({ ...form, keyPem })}
          hint="Se cifra (AES-256-GCM) antes de guardarse — nunca se muestra de nuevo."
        />
        {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button type="submit" loading={register.isPending} disabled={!form.alias || !form.certPem || !form.keyPem}><CheckCircle2 size={14} /> Registrar</Button>
        </div>
      </form>
    </Modal>
  );
}

function CertificatesSection({ clientId, canRevokeActive }) {
  const { data, isLoading } = useCertificates(clientId);
  const revoke = useRevokeCertificate(clientId);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [completingCert, setCompletingCert] = useState(null);

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-text-secondary flex items-center gap-2"><ShieldCheck size={16} /> Certificados</h3>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setRegisterOpen(true)}><Plus size={14} /> Cargar existente</Button>
          <Button variant="secondary" onClick={() => setGenerateOpen(true)}><KeyRound size={14} /> Generar nuevo</Button>
        </div>
      </div>
      <Table
        loading={isLoading}
        data={data}
        emptyMessage="Sin certificados cargados"
        columns={[
          { key: 'alias', label: 'Alias' },
          { key: 'environment', label: 'Entorno', render: (r) => <Badge variant={ENV_VARIANT[r.environment]}>{r.environment}</Badge> },
          { key: 'cuit', label: 'CUIT', render: (r) => r.cuit || '—' },
          { key: 'fingerprint', label: 'Fingerprint', render: (r) => r.fingerprint ? <span className="font-mono text-xs">{r.fingerprint.slice(0, 16)}…</span> : '—' },
          { key: 'valid_to', label: 'Vence', render: (r) => r.valid_to ? new Date(r.valid_to).toLocaleDateString('es-AR') : '—' },
          { key: 'status', label: 'Estado', render: (r) => <Badge variant={r.status === 'active' ? 'success' : r.status === 'pending' ? 'warning' : 'neutral'}>{r.status}</Badge> },
          {
            key: 'actions', label: '', render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                {r.status === 'pending' && (
                  <>
                    <button
                      onClick={() => setCompletingCert(r)}
                      className="px-2 py-1 rounded-lg border border-border-strong hover:bg-bg-elevated text-xs font-medium whitespace-nowrap"
                    >
                      Completar
                    </button>
                    <button
                      onClick={() => { if (confirm(`¿Eliminar el certificado "${r.alias}"? Todavía no fue sincronizado con ARCA — se puede borrar sin problema.`)) revoke.mutate(r.id); }}
                      className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-red-400" title="Eliminar"
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
                {r.status === 'active' && canRevokeActive && (
                  <button
                    onClick={() => { if (confirm(`¿Revocar el certificado "${r.alias}"? Deja de usarse para operar contra ARCA.`)) revoke.mutate(r.id); }}
                    className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-amber-400" title="Revocar"
                  >
                    <Ban size={15} />
                  </button>
                )}
              </div>
            ),
          },
        ]}
      />
      <GenerateCsrWizard clientId={clientId} open={generateOpen} onClose={() => setGenerateOpen(false)} />
      <RegisterCertificateModal clientId={clientId} open={registerOpen} onClose={() => setRegisterOpen(false)} />
      <CompleteCertificateModal clientId={clientId} certificate={completingCert} onClose={() => setCompletingCert(null)} />
    </div>
  );
}

function SyncCell({ posId, environment, syncedAt, onSync, syncing }) {
  if (syncedAt) {
    return (
      <Badge variant="success" title={new Date(syncedAt).toLocaleString()}>
        {environment === 'testing' ? 'testing ✓' : 'producción ✓'}
      </Badge>
    );
  }
  return (
    <Button variant="ghost" className="text-xs" loading={syncing} onClick={() => onSync(posId, environment)}>
      Sincronizar {environment === 'testing' ? 'testing' : 'producción'}
    </Button>
  );
}

function PointsOfSaleSection({ clientId }) {
  const { data, isLoading } = usePointsOfSale(clientId);
  const create = useCreatePos(clientId);
  const sync = useSyncPos(clientId);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pvNumber: '', description: '' });
  const [syncError, setSyncError] = useState(null);
  const [syncingKey, setSyncingKey] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await create.mutateAsync({ ...form, pvNumber: Number(form.pvNumber) });
    setOpen(false);
    setForm({ pvNumber: '', description: '' });
  };

  const handleSync = async (posId, environment) => {
    setSyncError(null);
    setSyncingKey(`${posId}:${environment}`);
    try {
      await sync.mutateAsync({ posId, environment });
    } catch (err) {
      setSyncError(err.response?.data?.error || 'No se pudo sincronizar con ARCA');
    } finally {
      setSyncingKey(null);
    }
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-text-secondary">Puntos de venta</h3>
        <Button variant="secondary" onClick={() => setOpen(true)}><Plus size={14} /> Nuevo</Button>
      </div>
      <p className="text-xs text-text-muted">
        Un punto de venta pertenece al CUIT, no a un certificado ni a un entorno — no se borra si
        el certificado vence o se renueva. Lo que varía por entorno es si el circuito de testing
        o de producción ya fue confirmado contra ese PV.
      </p>
      <Table
        loading={isLoading}
        data={data}
        emptyMessage="Sin puntos de venta"
        columns={[
          { key: 'pv_number', label: 'Nro' },
          { key: 'description', label: 'Descripción' },
          {
            key: 'synced_testing_at', label: 'Testing', render: (r) => (
              <SyncCell posId={r.id} environment="testing" syncedAt={r.synced_testing_at}
                onSync={handleSync} syncing={syncingKey === `${r.id}:testing`} />
            ),
          },
          {
            key: 'synced_production_at', label: 'Producción', render: (r) => (
              <SyncCell posId={r.id} environment="production" syncedAt={r.synced_production_at}
                onSync={handleSync} syncing={syncingKey === `${r.id}:production`} />
            ),
          },
          { key: 'status', label: 'Estado', render: (r) => <Badge variant={r.status === 'active' ? 'success' : 'danger'}>{r.status}</Badge> },
        ]}
      />
      {syncError && (
        <p className="text-xs text-red-400">
          {typeof syncError === 'string' ? syncError : JSON.stringify(syncError)}
        </p>
      )}
      <p className="text-xs text-text-muted">
        "Sincronizar" confirma contra ARCA que el punto de venta está operable para facturar en
        ese entorno. Si falla con un error de autorización, puede ser que falte habilitar el
        servicio WSFEv1 para este CUIT en el Administrador de Relaciones de Clave Fiscal de ARCA.
      </p>
      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo punto de venta">
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input label="Número de punto de venta" type="number" min="1" step="1" value={form.pvNumber} onChange={(e) => setForm({ ...form, pvNumber: e.target.value })} required />
          <Input label="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={create.isPending}>Crear</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function ApiKeysSection({ clientId }) {
  const { data, isLoading } = useApiKeys(clientId);
  const generate = useGenerateApiKey(clientId);
  const revoke = useRevokeApiKey(clientId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [newKey, setNewKey] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await generate.mutateAsync({ name });
    setNewKey(result.key);
    setName('');
  };

  const closeModal = () => {
    setOpen(false);
    setNewKey(null);
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-text-secondary">API Keys</h3>
        <Button variant="secondary" onClick={() => setOpen(true)}><Plus size={14} /> Generar</Button>
      </div>
      <Table
        loading={isLoading}
        data={data}
        emptyMessage="Sin API keys"
        columns={[
          { key: 'name', label: 'Nombre' },
          { key: 'key_prefix', label: 'Prefijo', render: (r) => <span className="font-mono text-xs">{r.key_prefix}…</span> },
          { key: 'last_used_at', label: 'Último uso', render: (r) => r.last_used_at ? new Date(r.last_used_at).toLocaleString('es-AR') : 'nunca' },
          {
            key: 'actions', label: '', render: (r) => !r.revoked_at && (
              <button onClick={() => { if (confirm('¿Revocar esta API Key? Deja de funcionar de inmediato.')) revoke.mutate(r.id); }} className="text-red-400 hover:text-red-300" title="Revocar">
                <Ban size={14} />
              </button>
            ),
          },
        ]}
      />
      <Modal open={open} onClose={closeModal} title="Generar API Key">
        {!newKey ? (
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input label="Nombre descriptivo" value={name} onChange={(e) => setName(e.target.value)} placeholder="ERP producción" required />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={closeModal}>Cancelar</Button>
              <Button type="submit" loading={generate.isPending}>Generar</Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-amber-400">⚠ Guardá esta key ahora — no se vuelve a mostrar.</p>
            <code className="block text-xs font-mono bg-bg-elevated p-3 rounded-lg break-all">{newKey}</code>
            <div className="flex justify-end pt-2">
              <Button onClick={closeModal}>Listo</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuthStore();
  const { data: client, isLoading } = useClient(id);
  const isSuperadmin = user?.role === 'superadmin';

  if (isLoading) return null;
  if (!client) return <p className="text-text-muted">Cliente no encontrado.</p>;

  return (
    <div className="space-y-6">
      {isSuperadmin && (
        <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-primary">
          <ArrowLeft size={14} /> Volver a clientes
        </Link>
      )}
      <PageHeader title={client.business_name} subtitle={`${client.code} · CUIT ${client.cuit}`} />
      <CertificatesSection clientId={id} canRevokeActive={isSuperadmin} />
      <PointsOfSaleSection clientId={id} />
      {isSuperadmin && <ApiKeysSection clientId={id} />}
    </div>
  );
}
